import {
  apiListQuery,
  apiQuery,
  createQueryKeys,
  useApiMutation,
  type ApiMutationOptions,
  type ApiMutationResult,
} from '@/shared/data';
import type { StepUpGrant } from '@/shared/http';
import { getCurrentTenant } from '@/shared/http/client';
import * as api from './user.api';
import type { UserWrite } from './user.wire';
const keys = createQueryKeys('user');
export const userKeys = keys;
export function userOptionsQuery(
  params: { tenant?: string | undefined; name?: string | undefined } = {},
) {
  const resolved = { name: params.name, tenant: params.tenant ?? getCurrentTenant() };
  return apiListQuery(keys.custom('options', 'user', resolved), ({ signal }) =>
    api.listUserOptions(resolved, signal),
  );
}

export function userDetailQuery(id: string) {
  const tenant = getCurrentTenant();
  return apiQuery(keys.custom('detail', id, { tenant }), ({ signal }) => api.getUser(id, tenant, signal), {
    enabled: id !== '' && id !== 'novo',
  });
}

const tenant = () => getCurrentTenant();
/** Toda escrita de Segurança leva a autorização do step-up novo (`X-UY3-StepUp`), pedida pela tela. */
type Authorized = { readonly stepUp: StepUpGrant };
export const userMutations = {
  create: ({ body, stepUp }: { body: UserWrite } & Authorized) => api.createUser(body, tenant(), stepUp),
  update: ({ id, body, stepUp }: { id: string; body: UserWrite } & Authorized) => api.updateUser(id, body, tenant(), stepUp),
  resetMfa: ({ id, stepUp }: { id: string } & Authorized) => api.disableUserMfa(id, tenant(), stepUp),
  resetPassword: ({ id, stepUp }: { id: string } & Authorized) => api.resetUserPassword(id, tenant(), stepUp),
  enable: ({ id, stepUp }: { id: string } & Authorized) => api.enableOrDisableUser(id, 'EnableUser', tenant(), stepUp),
  disable: ({ id, stepUp }: { id: string } & Authorized) => api.enableOrDisableUser(id, 'DisableUser', tenant(), stepUp),
  insertGroup: ({ id, groupName, stepUp }: { id: string; groupName: string } & Authorized) =>
    api.insertUserInGroup(groupName, id, tenant(), stepUp),
  removeGroup: ({ id, groupName, stepUp }: { id: string; groupName: string } & Authorized) =>
    api.removeUserFromGroup(groupName, id, tenant(), stepUp),
};
type Name = keyof typeof userMutations;
type Variables<K extends Name> = Parameters<(typeof userMutations)[K]>[0];
type Result<K extends Name> = Awaited<ReturnType<(typeof userMutations)[K]>>;

/** Um hook genérico por módulo (X07); o feedback é da tela (X06). */
export function useUserMutation<K extends Name>(
  name: K,
  options: Omit<ApiMutationOptions<Result<K>, Variables<K>>, 'mutationFn'> = {},
): ApiMutationResult<Result<K>, Variables<K>> {
  const run = userMutations[name] as (variables: Variables<K>) => Promise<Result<K>>;
  return useApiMutation({
    mutationKey: ['user', 'mutation', name],
    invalidate: [keys.all],
    success: false,
    error: false,
    ...options,
    mutationFn: run,
  });
}
